import { useEffect, useState } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { routes } from '@/utils/routes'

export type WebTabKey = 'details' | 'quiz' | 'activations' | 'email-sends' | 'meetings' | 'calendar' | 'leads' | 'media' | 'landing-page' | 'settings'

const tabPatterns: Array<{ tab: WebTabKey; pattern: RegExp }> = [
    { tab: 'leads', pattern: /\/web\/[^/]+\/leads(\/companies)?$/ },
    { tab: 'activations', pattern: /\/web\/[^/]+\/activations$/ },
    { tab: 'email-sends', pattern: /\/web\/[^/]+\/email-sends$/ },
    { tab: 'meetings', pattern: /\/web\/[^/]+\/meetings$/ },
    // Meeting detail (ORB-436) — the routed full-page view overlays the Meetings tab, so keep it active.
    { tab: 'meetings', pattern: /\/web\/[^/]+\/meeting\/[^/]+$/ },
    { tab: 'calendar', pattern: /\/web\/[^/]+\/calendar$/ },
    { tab: 'media', pattern: /\/web\/[^/]+\/media$/ },
    { tab: 'landing-page', pattern: /\/web\/[^/]+\/landing-page$/ },
    { tab: 'settings', pattern: /\/web\/[^/]+\/settings$/ },
    { tab: 'quiz', pattern: /\/web\/[^/]+\/quiz$/ },
    { tab: 'details', pattern: /\/web\/[^/]+$/ },
]

interface UseWebTabRouterParams {
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    webId: string
}

export function useWebTabRouter({ clientCompanyId, workspaceId, campaignId, webId }: UseWebTabRouterParams): {
    activeTab: WebTabKey
    routeToTab: (tab: WebTabKey) => void
} {
    const location = useLocation()
    const [activeTab, setActiveTab] = useState<WebTabKey>('details')

    useEffect(() => {
        const rawPath = (location as unknown as { path?: string; url?: string })?.path
            || (location as unknown as { path?: string; url?: string })?.url
            || (typeof window !== 'undefined' ? window.location.pathname : '')
        for (const { tab, pattern } of tabPatterns) {
            if (pattern.test(rawPath) && activeTab !== tab) {
                setActiveTab(tab)
                break
            }
        }
    }, [location])

    const scoped = routes.scoped(clientCompanyId, workspaceId)

    const routeToTab = (tab: WebTabKey) => {
        setActiveTab(tab)
        const basePath = scoped.web(campaignId, webId)
        const target = tab === 'details' ? basePath : `${basePath}/${tab}`
        try { location.route(target) } catch { if (typeof window !== 'undefined') window.history.pushState({}, '', target) }
    }

    return { activeTab, routeToTab }
}
